import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import { Toast } from './toast';
import type { ToastData } from './toastProvider';

interface ToastContainerProps {
  toasts: ToastData[];
  onClose: (id: string) => void;
}

const EXIT_ANIMATION_DURATION = 200; // ms

export function ToastContainer({ toasts, onClose }: ToastContainerProps): ReactNode {
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
  const [visibleToasts, setVisibleToasts] = useState<ToastData[]>(toasts);
  const prevToastsRef = useRef<ToastData[]>(toasts);

  useEffect(() => {
    const prevToasts = prevToastsRef.current;
    const currentToastIds = new Set(toasts.map((t) => t.id));
    const removedIds = prevToasts.filter((t) => !currentToastIds.has(t.id)).map((t) => t.id);

    if (removedIds.length > 0) {
      setVisibleToasts((prev) => prev.filter((t) => !removedIds.includes(t.id)));
      setExitingIds((prev) => {
        const newSet = new Set(prev);
        for (const id of removedIds) {
          newSet.add(id);
        }
        return newSet;
      });

      setTimeout(() => {
        setExitingIds((prev) => {
          const newSet = new Set(prev);
          for (const id of removedIds) {
            newSet.delete(id);
          }
          return newSet;
        });
        setVisibleToasts(toasts);
      }, EXIT_ANIMATION_DURATION);
    } else {
      setVisibleToasts((prev) => {
        const currentIds = new Set(toasts.map((t) => t.id));
        const prevIds = new Set(prev.map((t) => t.id));
        const newToasts = toasts.filter((t) => !prevIds.has(t.id));
        const existingToasts = prev.filter((t) => currentIds.has(t.id));
        return [...existingToasts, ...newToasts];
      });
    }

    prevToastsRef.current = toasts;
  }, [toasts]);

  const handleClose = (id: string) => {
    setVisibleToasts((prev) => prev.filter((t) => t.id !== id));
    setExitingIds((prev) => new Set([...prev, id]));

    setTimeout(() => {
      onClose(id);
      setExitingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, EXIT_ANIMATION_DURATION);
  };

  if (visibleToasts.length === 0 && exitingIds.size === 0) {
    return null;
  }

  const activeToasts = visibleToasts.filter((t) => !exitingIds.has(t.id));
  const exitingToasts = prevToastsRef.current.filter(
    (t) => exitingIds.has(t.id) && !visibleToasts.some((vt) => vt.id === t.id)
  );
  const displayToasts = [...activeToasts, ...exitingToasts];

  return (
    <div
      className="fixed top-4 right-4 left-4 sm:left-auto z-100 flex flex-col gap-2 w-[calc(100%-2rem)] sm:w-auto sm:max-w-md pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      {displayToasts.map((toast) => {
        const isExiting = exitingIds.has(toast.id);
        return (
          <div
            key={toast.id}
            className={cn('pointer-events-auto w-full', isExiting ? 'toast-exit' : 'toast-enter')}
          >
            <Toast
              id={toast.id}
              title={toast.title}
              message={toast.message}
              variant={toast.variant}
              duration={toast.duration}
              onClose={handleClose}
            />
          </div>
        );
      })}
    </div>
  );
}
